import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './NotificationView.module.css';
import { useNotificationStore } from '../../Store/notificationStore';
import {
  calenderCheck,
  fetchAgentDetailById,
  getAgentSpamSettings,
  markNotificationAsSeen,
} from '../../Store/apiStore';
import { fetchSetupScore } from '../../lib/setupScore';
import decodeToken from '../../lib/decodeToken';
import PopUp from '../Popup/Popup';

const parsePayload = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const extractVoipNumber = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) return value[0]?.phone_number || value[0]?.number || value[0] || '';
  if (typeof value === 'object') return value.phone_number || value.number || '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) return trimmed;
    try {
      return extractVoipNumber(JSON.parse(trimmed));
    } catch {
      return trimmed;
    }
  }
  return '';
};

// Which Setup-Ready feature (if any) a notification is nudging the user to
// complete. Only classifies a notification as a setup feature when it is an
// ACTIONABLE setup nudge — an explicit backend setup signal, or setup-intent
// wording. This stops informational alerts that merely mention a feature word
// (e.g. "Spam Caller Blocked", "Payment Failure") from hijacking the click into
// a /setup-ready/* screen.
const matchFeature = (text) => {
  if (/spam|blacklist/.test(text)) return 'spam';
  if (/triage|call.?transfer/.test(text)) return 'triage';
  if (/forward/.test(text)) return 'forwarding';
  if (/calendar|calender/.test(text)) return 'calendar';
  if (/availability|business.?hours/.test(text)) return 'availability';
  if (/phone.?number|local.?number|assign.?number|number.?purchas/.test(text)) return 'phone';
  return null;
};

const resolveFeatureKey = (notification) => {
  const payload = parsePayload(notification?.dataPayload);

  // 1) Explicit backend setup signal always wins.
  const explicit = [payload.feature, payload.setupFeature, payload.setupKey, payload?.meta?.feature]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const explicitKey = matchFeature(explicit);
  if (explicitKey) return explicitKey;

  // 2) Otherwise infer from the copy ONLY when it reads like a setup nudge,
  //    not an informational alert.
  const text = `${notification?.title || ''} ${notification?.message || ''}`.toLowerCase();
  const isSetupNudge =
    /set ?up|complete|connect|finish|turn on|enable|configure|not live|almost there|know your people/.test(text);
  if (!isSetupNudge) return null;

  return matchFeature(text);
};

const resolveNotificationRoute = (notification, fallbackAgentId = '') => {
  const payload = parsePayload(notification?.dataPayload);
  const meta = payload?.meta && typeof payload.meta === 'object' ? payload.meta : {};
  const notificationAgentId =
    payload.agentId ||
    payload.agent_id ||
    meta.agentId ||
    meta.agent_id ||
    notification?.agentId ||
    notification?.agent_id ||
    fallbackAgentId;
  const callId =
    payload.callId ||
    payload.call_id ||
    meta.callId ||
    meta.call_id ||
    notification?.callId ||
    notification?.call_id;

  const explicitRoute = [
    notification?.clickAction,
    payload?.clickAction,
    payload?.route,
    payload?.path,
    payload?.url,
    payload?.deepLink,
    payload?.deep_link,
    meta?.clickAction,
    meta?.route,
    meta?.path,
  ].find((value) => typeof value === 'string' && value.trim());

  if (explicitRoute) {
    const route = explicitRoute.trim();
    if (route.startsWith('/')) return route;

    try {
      const parsed = new URL(route, window.location.origin);
      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch (_e) {
      // Invalid or unsupported deep links use the safe dashboard fallback.
    }
  }

  // A backend clickAction is authoritative. Only fall back to call details
  // when no explicit destination exists and every required context value is
  // available. Appointment notifications intentionally contain both a
  // calendar clickAction and callId; callId used to win and open CallDetails
  // without start_timestamp, leaving that screen loading forever.
  if (callId) {
    const startTimestamp =
      payload.start_timestamp ||
      payload.startTimestamp ||
      meta.start_timestamp ||
      meta.startTimestamp ||
      notification?.start_timestamp ||
      notification?.startTimestamp;

    if (notificationAgentId && startTimestamp) {
      const params = new URLSearchParams({
        agentId: String(notificationAgentId),
        start_timestamp: String(startTimestamp),
      });
      return `/call-details/${encodeURIComponent(callId)}?${params.toString()}`;
    }

    // Older call notifications often contain only callId. Call history is the
    // safest relevant destination because it does not require a fabricated
    // start timestamp.
    return notificationAgentId
      ? `/totalcall-list?agentId=${encodeURIComponent(notificationAgentId)}`
      : '/totalcall-list';
  }

  const notificationText = [
    notification?.type,
    notification?.title,
    notification?.message,
    payload?.kind,
    meta?.kind,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // New appointment / booking → calendar.
  if (/appointment|booking|booked|meeting (scheduled|booked)/.test(notificationText)) {
    return '/calendar';
  }
  // A message taken from a caller → call history.
  if (/message from|message taken|new message|left a message/.test(notificationText)) {
    return '/totalcall-list';
  }
  // Call activity (missed / urgent / spam-blocked / recordings) → call history.
  if (/call history|call recording|missed call|urgent call|call report|spam caller|caller (auto-?)?blocked/.test(notificationText)) {
    return '/totalcall-list';
  }
  // Billing / subscription / usage / minutes → plan selection.
  if (
    /subscription|payment (failed|failure)|billing|usage alert|free minutes|minutes (running low|are almost)|upgrade your plan|\d+\s*%[^.]*minutes|minutes[^.]*used|plan minutes/.test(
      notificationText
    )
  ) {
    const params = new URLSearchParams({ mode: 'plans' });
    if (notificationAgentId) params.set('agentId', String(notificationAgentId));
    return `/recommended-plan?${params.toString()}`;
  }
  // Lead assignment → sales board.
  if (/lead assigned|new lead/.test(notificationText)) {
    return '/sales&marketing/kanban/board/';
  }
  // Support ticket → tickets screen.
  if (/ticket/.test(notificationText)) {
    return '/raise-tickets';
  }
  // Contact sync → contacts.
  if (/contact sync|sync your contacts|know your people/.test(notificationText)) {
    return '/contacts';
  }

  // Agent-created / welcome / genuinely generic notifications → agent dashboard.
  // Guarantees every notification still has a safe, working View action even
  // when an older API record has no clickAction.
  return '/fast-agent-detail';
};

// True when the notification carries its own explicit destination (a clickAction,
// a call id, or a route in its payload). Such notifications must navigate to THAT
// destination, never get re-interpreted as a Setup-Ready feature CTA.
const hasExplicitDestination = (notification) => {
  const payload = parsePayload(notification?.dataPayload);
  const meta = payload?.meta && typeof payload.meta === 'object' ? payload.meta : {};
  const callId =
    payload.callId || payload.call_id || meta.callId || meta.call_id ||
    notification?.callId || notification?.call_id;
  if (callId) return true;
  return [
    notification?.clickAction, payload?.clickAction, payload?.route, payload?.path,
    payload?.url, payload?.deepLink, payload?.deep_link, meta?.clickAction, meta?.route, meta?.path,
  ].some((value) => typeof value === 'string' && value.trim());
};

const FEATURE_CONFIG = {
  phone: { label: 'AI Local phone number', route: '/setup-ready/ai-local-number', tone: 'green' },
  forwarding: { label: 'Business call forwarding', route: '/setup-ready/call-forwarding', tone: 'amber' },
  triage: { label: 'Internal Call Triage', route: '/setup-ready/call-triage', tone: 'amber' },
  calendar: { label: 'Calendar', route: '/setup-ready/calendar', tone: 'green' },
  spam: { label: 'Turn on spam filtering', route: '/setup-ready/spam-filtering', tone: 'amber' },
  availability: { label: 'Availability', route: '/setup-ready/availability', tone: 'green', updatable: true },
};

// ── Notification ke icon (design ke hisaab se) ──────────────────────────
// Chaar tarah ke rangeen, bhare hue icon, har ek ki apni tile:
//   call     → narangi tile, phone (+)        — call forwarding, number, calls
//   usage    → halki purple tile, pie chart   — usage, minutes, billing, plan
//   calendar → halki hari tile, calendar      — appointments, bookings, calendar
//   tips     → peeli tile, bulb               — tips, welcome, baaki sab
// (spam ke liye purani shield hi hai — design me uska alag icon nahi tha.)
//
// Kaun sa icon: pehle Setup-Ready feature se (agar notification usi ka hai),
// warna title + message ke shabdon se.
const ICON_TONE = {
  call: 'orange',
  usage: 'purple',
  calendar: 'green',
  tips: 'yellow',
  spam: 'purple',
};

const resolveIconKind = (notification, featureKey) => {
  if (featureKey === 'spam') return 'spam';
  if (featureKey === 'phone' || featureKey === 'forwarding' || featureKey === 'triage') return 'call';
  if (featureKey === 'calendar' || featureKey === 'availability') return 'calendar';

  const text = `${notification?.type || ''} ${notification?.title || ''} ${notification?.message || ''}`.toLowerCase();
  if (/appointment|booking|booked|calendar|calender|meeting/.test(text)) return 'calendar';
  if (/usage|minutes|billing|subscription|payment|invoice|upgrade|\bplan\b/.test(text)) return 'usage';
  if (/spam|blacklist|blocked/.test(text)) return 'spam';
  if (/call|forward|voicemail|missed|message from|phone number/.test(text)) return 'call';
  return 'tips';
};

const NotificationIcon = ({ kind }) => {
  if (kind === 'usage') {
    // Pie chart: bhara hua gola, teen safed kaat aur beech me chhed.
    return (
      <svg className={styles.filledIcon} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9.5" fill="#6524EB" />
        <path d="M12 2.5V12M12 12l8.2 4.7M12 12l-8.2 4.7" stroke="#EEE9FF" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="12" r="3.2" fill="#EEE9FF" />
      </svg>
    );
  }
  if (kind === 'calendar') {
    return (
      <svg className={styles.filledIcon} viewBox="0 0 24 24" aria-hidden="true">
        <rect x="7" y="2.5" width="2.2" height="4.5" rx="1.1" fill="#1E9E57" />
        <rect x="14.8" y="2.5" width="2.2" height="4.5" rx="1.1" fill="#1E9E57" />
        <rect x="3" y="4.5" width="18" height="16.5" rx="3" fill="#22B365" />
        <rect x="5" y="9.2" width="14" height="9.8" rx="1.4" fill="#E3F6EA" />
        <g fill="#22B365">
          <rect x="6.6" y="10.8" width="2.6" height="2.4" rx="0.5" />
          <rect x="10.7" y="10.8" width="2.6" height="2.4" rx="0.5" />
          <rect x="14.8" y="10.8" width="2.6" height="2.4" rx="0.5" />
          <rect x="6.6" y="14.9" width="2.6" height="2.4" rx="0.5" />
          <rect x="10.7" y="14.9" width="2.6" height="2.4" rx="0.5" />
          <rect x="14.8" y="14.9" width="2.6" height="2.4" rx="0.5" />
        </g>
      </svg>
    );
  }
  if (kind === 'tips') {
    // Bulb: peela sheesha, gehra base, upar roshni ki kirnen.
    return (
      <svg className={styles.filledIcon} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5.2a5.6 5.6 0 0 0-3.4 10c.6.5.9 1 .9 1.7v.4h5v-.4c0-.7.3-1.2.9-1.7A5.6 5.6 0 0 0 12 5.2z" fill="#FFC83D" stroke="#24252C" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M9.8 19.2h4.4M10.5 21.2h3" stroke="#24252C" strokeWidth="1.3" strokeLinecap="round" />
        <path d="M12 1.3v1.7M5.1 3.9l1.2 1.2M18.9 3.9l-1.2 1.2M2.6 10.3h1.7M19.7 10.3h1.7" stroke="#24252C" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M10.3 9.2a2.3 2.3 0 0 1 1.7-.9" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'spam') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3l7 3v5c0 4.6-2.9 8.2-7 10-4.1-1.8-7-5.4-7-10V6l7-3z" />
        <path d="M9 12l2 2 4-5" />
      </svg>
    );
  }
  // call: bhara hua narangi phone (wahi shape jo is file ke purane icon me thi),
  // upar daayein gol ghere me "+".
  return (
    <svg className={styles.filledIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 4l3 1 1.5 3-2 1.5a10 10 0 0 0 6 6l1.5-2 3 1.5 1 4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 3-2z" fill="#F97316" stroke="#F97316" strokeWidth="1.4" strokeLinejoin="round" transform="translate(-0.6 2.2) scale(0.9)" />
      <circle cx="17.2" cy="7" r="5" fill="#F97316" stroke="#FFE1B4" strokeWidth="1.6" />
      <path d="M17.2 4.7v4.6M14.9 7h4.6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
};

// Teen tab. "Action Needed" = jin notifications ke khulne par button aata hai
// (View / Setup Now / Update) — user ko kuch karna hai. "Updates" = jin par
// koi button nahi (sirf jaankari, jaise usage / billing alerts). "All" = dono.
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'action', label: 'Action Needed' },
  { key: 'updates', label: 'Updates' },
];

const formatRelativeTime = (value) => {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const NotificationView = ({ onAction }) => {
  const navigate = useNavigate();
  const storedNotifications = useNotificationStore((state) => state.notifications);
  const notifications = useMemo(
    () => (Array.isArray(storedNotifications) ? storedNotifications : []),
    [storedNotifications]
  );
  const setNotifications = useNotificationStore((state) => state.setNotifications);
  const [expandedId, setExpandedId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(30);
  const [featureStatus, setFeatureStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [popupMessage, setPopupMessage] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [markingAll, setMarkingAll] = useState(false);

  const token = localStorage.getItem('token') || '';
  const decoded = token ? decodeToken(token) : null;
  const userId = localStorage.getItem('teamOwnerId') || decoded?.id;
  const agentId = sessionStorage.getItem('SelectAgentId');
  const businessId = sessionStorage.getItem('SelectAgentBusinessId');

  useEffect(() => {
    sessionStorage.setItem('naviateFrom', 'notifications');
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!agentId) {
      setStatusLoading(false);
      return undefined;
    }

    (async () => {
      setStatusLoading(true);
      const [agentBody, score, calendar, spam] = await Promise.all([
        fetchAgentDetailById({ agentId, bussinesId: businessId }).catch(() => null),
        fetchSetupScore(agentId).catch(() => null),
        userId ? calenderCheck(userId).catch(() => null) : Promise.resolve(null),
        getAgentSpamSettings(agentId).catch(() => ({})),
      ]);
      if (cancelled) return;

      const agent = agentBody?.data?.agent || null;
      setFeatureStatus({
        phone: Boolean(extractVoipNumber(agent?.voip_numbers)),
        forwarding: Boolean(score?.steps?.callForwardingDone?.done || agent?.callForwarding),
        triage: Boolean(score?.steps?.triageRulesSaved?.done),
        calendar: Boolean(calendar?.data?.connected),
        spam: Boolean(spam?.autoBlacklistEnabled),
        availability: true,
      });
      setStatusLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [agentId, businessId, userId]);

  const markRead = async (notification) => {
    if (!notification || notification.status === 'read') return;
    const response = await markNotificationAsSeen(notification.id, notification.type);
    if (!response?.success) return;
    setNotifications(
      notifications.map((item) =>
        item.id === notification.id ? { ...item, status: 'read' } : item
      )
    );
  };

  const toggleNotification = async (notification) => {
    const opening = expandedId !== notification.id;
    setExpandedId(opening ? notification.id : null);
    if (opening) await markRead(notification);
  };

  const openFeature = (featureKey) => {
    const config = FEATURE_CONFIG[featureKey];
    if (!config) return;

    const prerequisite =
      featureKey === 'forwarding' && !featureStatus?.phone
        ? FEATURE_CONFIG.phone.label
        : featureKey === 'triage' && !featureStatus?.phone
          ? FEATURE_CONFIG.phone.label
          : featureKey === 'spam' && !featureStatus?.phone
            ? FEATURE_CONFIG.phone.label
            : null;

    if (prerequisite) {
      setPopupMessage(`You must complete the previous step ${prerequisite} to access this.`);
      return;
    }

    if (config.route === '/go-live-setup') {
      try {
        if (agentId) sessionStorage.setItem('SelectAgentId', agentId);
        if (businessId) sessionStorage.setItem('SelectAgentBusinessId', businessId);
        sessionStorage.setItem('goLiveWizard.assignNumberOnly', '1');
        localStorage.setItem('goLiveWizard.lastStep', String(config.wizardStep));
      } catch (_e) {
        // The destination wizard performs its own context validation.
      }
    }
    goToRoute(config.route);
  };

  // Open a notification's destination WITHOUT flashing the dashboard. This
  // NotificationView is an overlay ON TOP of /fast-agent-detail: closing the
  // panel (onAction) first reveals the dashboard for a beat before navigate
  // lands — the ~1s "fast-agent-detail flash" QA saw. For a CROSS-route target
  // the navigate itself unmounts FastAgentDetails (and this overlay with it),
  // so we must NOT close the panel first. Only for a same-route
  // /fast-agent-detail target (no unmount) do we close the panel so the
  // dashboard, not the still-open panel, is shown.
  const goToRoute = (route, notification = null) => {
    if (!route) return;
    const payload = parsePayload(notification?.dataPayload);
    const meta = payload?.meta && typeof payload.meta === 'object' ? payload.meta : {};
    let routeAgentId = '';
    try {
      routeAgentId = new URL(route, window.location.origin).searchParams.get('agentId') || '';
    } catch (_e) {
      // The resolver returns internal routes; retain current agent on failure.
    }
    const destinationAgentId =
      payload.agentId ||
      payload.agent_id ||
      meta.agentId ||
      meta.agent_id ||
      notification?.agentId ||
      notification?.agent_id ||
      routeAgentId ||
      agentId;

    if (destinationAgentId) {
      sessionStorage.setItem('SelectAgentId', String(destinationAgentId));
      sessionStorage.setItem('agentId', String(destinationAgentId));
    }

    const staysOnDashboard = route.split('?')[0] === '/fast-agent-detail';
    if (staysOnDashboard) onAction?.();
    navigate(route);
  };

  const openNotification = (notification) => {
    goToRoute(resolveNotificationRoute(notification, agentId), notification);
  };

  // Har notification ke liye: kis feature ka hai, button ka text, aur sabse
  // zaroori — khulne par button aata hai ya nahi (`hasAction`). Tabs isi se
  // bante hain. Ye wahi hisaab hai jo pehle list ke andar har row pe hota tha;
  // bas ek jagah nikaal liya taaki tabs aur ginti bhi ise istemal kar saken.
  const describe = (notification) => {
    // A notification with its own explicit destination (clickAction /
    // callId / payload route) navigates THERE — it must not be turned
    // into a Setup-Ready feature CTA just because its copy mentions a
    // feature word. Only clickAction-less notifications may resolve to a
    // setup feature.
    const explicitDest = hasExplicitDestination(notification);
    let featureKey = explicitDest ? null : resolveFeatureKey(notification);
    // Generic setup reminders (for example "Your AI receptionist is
    // not live yet") do not always carry a feature key. Point them at
    // the first unfinished Setup Ready feature so their CTA is still
    // relevant instead of falling back to a generic destination.
    if (!explicitDest && !featureKey && featureStatus) {
      const genericSetupText = `${notification.title || ''} ${notification.message || ''}`.toLowerCase();
      if (/not live|finish setup|setup incomplete|complete setup/.test(genericSetupText)) {
        featureKey = ['phone', 'forwarding', 'triage', 'calendar', 'spam']
          .find((key) => !featureStatus[key]) || null;
      }
    }
    const config = FEATURE_CONFIG[featureKey] || { tone: 'purple' };
    const featureDone = featureKey ? Boolean(featureStatus?.[featureKey]) : false;
    const actionRequested = /setup|complete|connect|assign|forward|not live|turn on|update/i.test(
      `${notification.title || ''} ${notification.message || ''}`
    );
    const payload = parsePayload(notification.dataPayload);
    const fallbackCta = payload?.ctaText || 'View';
    const featureCta = config.updatable && featureDone
      ? 'Update'
      : actionRequested || !featureDone
        ? 'Setup Now'
        : fallbackCta;

    // Usage / billing alerts (no clickAction, no feature) resolve to the
    // plan page. Per request they are INFORMATIONAL only — no "View"
    // button and NO navigation. The row still expands/collapses so the
    // user can read the full message; it just never routes anywhere.
    const directRoute = !featureKey
      ? resolveNotificationRoute(notification, agentId)
      : null;
    const isPlanNudge = Boolean(
      directRoute && directRoute.split('?')[0] === '/recommended-plan'
    );

    return {
      notification,
      featureKey,
      config,
      fallbackCta,
      featureCta,
      iconKind: resolveIconKind(notification, featureKey),
      // Button wala = "Action Needed"; bina button = "Updates".
      hasAction: !isPlanNudge,
      unread: notification.status !== 'read',
    };
  };

  const items = notifications.map(describe);
  const tabItems = {
    all: items,
    action: items.filter((item) => item.hasAction),
    updates: items.filter((item) => !item.hasAction),
  };
  const shown = tabItems[activeTab] || items;
  // "Action Needed" ka laal badge: jin pe abhi tak dhyan nahi gaya (unread).
  const actionUnread = tabItems.action.filter((item) => item.unread).length;
  const anyUnread = items.some((item) => item.unread);

  // Sab unread ko read karo. Backend me "sab ek saath" wala API nahi hai, isliye
  // har unread ke liye wahi single-read call jaata hai; jo safal hon sirf wahi
  // read dikhte hain.
  const markAllRead = async () => {
    if (markingAll) return;
    const unreadList = notifications.filter((item) => item.status !== 'read');
    if (unreadList.length === 0) return;
    setMarkingAll(true);
    try {
      const results = await Promise.all(
        unreadList.map((item) =>
          markNotificationAsSeen(item.id, item.type)
            .then((response) => (response?.success ? item.id : null))
            .catch(() => null)
        )
      );
      const done = new Set(results.filter((id) => id !== null));
      if (done.size > 0) {
        // Store ki taaza list pe lagao — is beech koi naya notification aaya ho
        // to wo na kate.
        const latest = useNotificationStore.getState().notifications;
        setNotifications(
          (Array.isArray(latest) ? latest : notifications).map((item) =>
            done.has(item.id) ? { ...item, status: 'read' } : item
          )
        );
      }
    } finally {
      setMarkingAll(false);
    }
  };

  const emptyText =
    notifications.length === 0
      ? 'No notifications available'
      : activeTab === 'action'
        ? 'Nothing needs your action right now'
        : 'No updates yet';

  return (
    <section className={styles.notificationContainer}>
      <div className={styles.listHeader}>
        <h2 className={styles.heading}>Notifications</h2>
        {anyUnread && (
          <button
            type="button"
            className={styles.markAllButton}
            onClick={markAllRead}
            disabled={markingAll}
          >
            {markingAll ? 'Marking…' : 'Mark all as read'}
          </button>
        )}
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Notification filters">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
            onClick={() => {
              setActiveTab(tab.key);
              setExpandedId(null);
              setVisibleCount(30);
            }}
          >
            {tab.label}
            {tab.key === 'action' && actionUnread > 0 && (
              <span className={styles.tabBadge} aria-label={`${actionUnread} unread`}>
                {actionUnread > 99 ? '99+' : actionUnread}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className={styles.notificationList}>
        {shown.length === 0 ? (
          <p className={styles.noNotifications}>{emptyText}</p>
        ) : (
          shown.slice(0, visibleCount).map(({ notification, featureKey, iconKind, fallbackCta, featureCta, hasAction, unread }) => {
            const expanded = expandedId === notification.id;

            return (
              <article
                key={notification.id}
                className={`${styles.notificationItem} ${expanded ? styles.expanded : ''}`}
              >
                <button
                  type="button"
                  className={styles.notificationRow}
                  onClick={() => toggleNotification(notification)}
                  aria-expanded={expanded}
                >
                  <span className={`${styles.iconTile} ${styles[ICON_TONE[iconKind]]}`}>
                    <NotificationIcon kind={iconKind} />
                  </span>
                  <span className={styles.notificationBody}>
                    <span className={styles.notificationTitle}>
                      {notification.title || 'System Notification'}
                    </span>
                    <span className={`${styles.notificationPreview} ${expanded ? styles.notificationPreviewOpen : ''}`}>
                      {notification.message}
                    </span>
                    <span className={styles.notificationTime}>{formatRelativeTime(notification.createdAt)}</span>
                  </span>
                  {unread && <span className={styles.unreadDot} aria-label="Unread" />}
                  {/* Teer sirf un rows pe jinke khulne par button aata hai. */}
                  {hasAction && (
                    <svg className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`} viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  )}
                </button>

                {expanded && hasAction && (
                  <div className={styles.notificationDetails}>
                    {featureKey && !statusLoading ? (
                      <button type="button" className={styles.setupButton} onClick={() => openFeature(featureKey)}>
                        {featureCta}
                      </button>
                    ) : (
                      <button type="button" className={styles.setupButton} onClick={() => openNotification(notification)}>
                        {fallbackCta}
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })
        )}

        {visibleCount < shown.length && (
          <button className={styles.loadMoreButton} onClick={() => setVisibleCount((count) => count + 50)}>
            Load More
          </button>
        )}
      </div>

      {popupMessage && (
        <PopUp type="failed" message={popupMessage} onClose={() => setPopupMessage('')} />
      )}
    </section>
  );
};

export default NotificationView;
